import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext();
const STORAGE_KEY = 'sdny_auth_v1';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setUser(parsed.user);
        setToken(parsed.token);
      }
    } catch (e) {
      console.warn('Failed to load auth', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!loading && user && token) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ user, token }));
    }
  }, [user, token, loading]);

  const login = async (email, password) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/auth/login`, { email, password });
      setUser({ email: res.data.email, name: res.data.name, phone: res.data.phone, id: res.data.user_id });
      setToken(res.data.token);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Login failed');
      throw e; // Re-throw to let the caller know login failed
    }
  };

  const register = async (email, password, name, phone) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/auth/register`, { email, password, name, phone });
      setUser({ email: res.data.email, name: res.data.name, phone: res.data.phone, id: res.data.user_id });
      setToken(res.data.token);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Registration failed');
      throw e; // Re-throw to let the caller know registration failed
    }
  };

  const logout = () => {
    try {
      setUser(null);
      setToken(null);
      localStorage.removeItem(STORAGE_KEY);
      toast.success('Logged out');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
